/**
 * The DATASET endpoints — the source a widget is built on (CT-5725).
 *
 * The rev4 flow splits authoring in two. The Data step's selection (entity,
 * columns, related sources and base filters) becomes a **published
 * `RECORD_SET` dataset of plain columns**, and the widget is then created on
 * top of it with `dataSource.mode = EXISTING_QUERY` and bindings that point at
 * the dataset's own `fieldKey`s. The aggregations live in the widget, never in
 * the dataset.
 *
 * `dataSource.mode = ENTITY` still works and still authors a dataset per
 * widget behind the scenes, but it is deprecated: it is kept until the joint
 * test with the backend, not as a second way of doing this.
 *
 * ## Create is idempotent, and a dataset is immutable
 *
 * The same selection answers with the same `datasetUuid` and `created: false`,
 * whatever order the columns or filters come in, so the builder may call it
 * again on every `Continue` without minting a dataset per keystroke. A
 * *different* selection makes **another** dataset: a published one is never
 * edited, and the widgets already built on it are left alone.
 *
 * Contract: `~/Downloads/dataset_frontend_contract.md` (2026-10-22), routes on
 * the dashboard aggregate — `create`, `{uuid}`, `list`.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards } from './apiClient.js';

export const DATASET_API_PREFIX = '/v0/dashboard/dataset';

/** `orderBy` values `/dataset/list` accepts. */
export const DATASET_ORDER_BY = Object.freeze(['name', 'code', 'updated_at']);

/**
 * One column of a published dataset.
 *
 * `fieldKey` is the alias the dataset exposes, and the only name a widget
 * binding may use as its `field`. The root's hidden `record_uuid` comes back
 * as a column too, flagged `isRowKey` and with no `fieldUuid`: it is the
 * backend's, not a field the author picked.
 */
export const mapApiDatasetColumnToUi = (raw = {}) => ({
  fieldUuid: raw.fieldUuid ?? null,
  sourceKey: raw.sourceKey ?? null,
  fieldKey: raw.fieldKey ?? '',
  isRowKey: Boolean(raw.isRowKey),
});

/** A dataset as `create` and `{uuid}` answer it. */
export const mapApiDatasetToUi = (raw = {}) => ({
  datasetUuid: raw.datasetUuid ?? '',
  code: raw.code ?? '',
  name: raw.name ?? '',
  status: raw.status ?? '',
  compositeEntityUuid: raw.compositeEntityUuid ?? '',
  /** `false` when the selection matched a dataset that already existed. */
  created: Boolean(raw.created),
  columns: (raw.columns ?? []).map(mapApiDatasetColumnToUi),
  filters: raw.filters ?? [],
});

/** A row of `/dataset/list`; it carries no columns. */
export const mapApiDatasetSummaryToUi = (raw = {}) => ({
  datasetUuid: raw.datasetUuid ?? '',
  code: raw.code ?? '',
  name: raw.name ?? '',
  status: raw.status ?? '',
  compositeEntityUuid: raw.compositeEntityUuid ?? '',
  updatedAt: raw.updatedAt ?? null,
});

/**
 * Create the dataset for a selection, or get back the one that already
 * describes it.
 *
 * @param {object} selection
 * @param {string} selection.compositeEntityUuid The composite the Data step chose.
 * @param {Array<{fieldUuid: string, sourceKey?: string, displayOrder: number}>}
 *   selection.columns Every field the widget reads — exposed columns, the
 *   groupers and the measures' fields — because the dataset is the only place
 *   they can come from.
 * @param {Array<object>} [selection.filters] Base filters, in the wire shape
 *   `{ fieldUuid, operator, value: { kind, literal } }`.
 * @param {Array<object>} [selection.sources] The join graph, multi-entity only.
 * @param {string} [selection.name] Display text; the backend mints the code.
 * @returns {Promise<object>} `mapApiDatasetToUi`.
 */
export async function createDataset({
  compositeEntityUuid,
  columns = [],
  filters = [],
  sources = [],
  name,
} = {}) {
  const response = await apiDashboards.post(`${DATASET_API_PREFIX}/create`, {
    ...(name ? { name } : {}),
    compositeEntityUuid,
    // Only multi-entity selections declare a graph; an empty list is the
    // single-entity case and the backend reads it as "just the root".
    sources,
    columns,
    filters,
  });
  const { body } = validateApiResponse(response, 'Could not prepare the data source.');
  return mapApiDatasetToUi(body ?? {});
}

/**
 * Read a published dataset — its columns and their `fieldKey`s.
 *
 * What reopening a saved widget needs: the widget stores the dataset's uuid,
 * and the aliases its bindings point at belong to the dataset, not to the
 * widget.
 */
export async function getDataset(datasetUuid) {
  const response = await apiDashboards.get(`${DATASET_API_PREFIX}/${datasetUuid}`);
  const { body } = validateApiResponse(response, 'Could not load the data source.');
  return mapApiDatasetToUi(body ?? {});
}

/**
 * List the workspace's published datasets.
 *
 * Includes the ones authored alongside an `ENTITY` widget (`DS_<widget code>`);
 * the widgets' own generated queries are never listed.
 */
export async function listDatasets({
  page = 1,
  pageSize = 20,
  orderBy,
  searchKeyword,
} = {}) {
  const response = await apiDashboards.post(`${DATASET_API_PREFIX}/list`, {
    page,
    pageSize,
    ...(orderBy ? { orderBy } : {}),
    ...(searchKeyword ? { searchKeyword } : {}),
  });
  const { body, meta } = validateApiResponse(response, 'Could not load the data sources.');
  return {
    datasets: (body ?? []).map(mapApiDatasetSummaryToUi),
    meta: normalizePagedMeta(meta),
  };
}

export default createDataset;
